import { createContext, useContext, useMemo } from 'react';
import {
  chakra,
  ComponentWithAs,
  forwardRef,
  HTMLChakraProps,
  omitThemingProps,
  ThemingProps,
  useMultiStyleConfig,
} from '@chakra-ui/react';
import { ColorName } from '@shinetools/sunshine';

import DetailSidebarBody from './Body';
import { COMPONENT_NAME, StylesProvider } from './context';
import DetailSidebarFooter from './Footer';
import DetailSidebarHeader from './Header';

type SubComponents = {
  Header: typeof DetailSidebarHeader;
  Body: typeof DetailSidebarBody;
  Footer: typeof DetailSidebarFooter;
};

export type DetailSidebarVariant = 'solid' | 'ghost';

interface Context {
  backTo?: string;
  closeTo?: string;
  onClose?: () => void;
  onBack?: () => void;
}

export type DetailSidebarProps = HTMLChakraProps<'aside'> &
  Context &
  Omit<ThemingProps<'DetailSidebar'>, 'colorScheme'> & {
    colorScheme?: ColorName;
  };

const DetailSidebarContext = createContext<Context | undefined>({});

export const useDetailSidebar = (): Context => {
  const context = useContext(DetailSidebarContext);

  if (!context) {
    throw new Error(
      'useDetailSidebar must be used within a <DetailSidebar> component',
    );
  }

  return context;
};

const DetailSidebar = forwardRef<DetailSidebarProps, 'aside'>(
  ({ backTo, children, closeTo, onBack, onClose, ...props }, ref) => {
    const context = useMemo<Context>(
      () => ({
        backTo,
        closeTo,
        onBack,
        onClose,
      }),
      [backTo, closeTo, onBack, onClose],
    );
    const styles = useMultiStyleConfig(COMPONENT_NAME, props);
    const rest = omitThemingProps(props);

    return (
      <DetailSidebarContext.Provider value={context}>
        <chakra.aside __css={styles.root} ref={ref} {...rest}>
          <StylesProvider value={styles}>{children}</StylesProvider>
        </chakra.aside>
      </DetailSidebarContext.Provider>
    );
  },
) as ComponentWithAs<'aside', DetailSidebarProps> & SubComponents;

DetailSidebar.Header = DetailSidebarHeader;
DetailSidebar.Body = DetailSidebarBody;
DetailSidebar.Footer = DetailSidebarFooter;

export default DetailSidebar;
